import React, { useState, useEffect } from 'react';
import {
  Stack,
  Paper,
  Grid,
  Checkbox,
  Text,
  Select,
  Group,
  Button,
  LoadingOverlay,
} from '@mantine/core';
import moment from 'moment';
import { convertTo24HourFormat } from '../../utils/timeUtils';
import { useMediaQuery } from '@mantine/hooks';

function BusinessHoursForm({ onSubmit, onClose, initialData, includeSkip = false, buttonText= 'Continue' }) {
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 500px)');
  const defaultHours = {
    sunday_open_time: null,
    sunday_end_time: null,
    monday_open_time: null,
    monday_end_time: null,
    tuesday_open_time: null,
    tuesday_end_time: null,
    wednesday_open_time: null,
    wednesday_end_time: null,
    thursday_open_time: null,
    thursday_end_time: null,
    friday_open_time: null,
    friday_end_time: null,
    saturday_open_time: null,
    saturday_end_time: null,
  };

  const [formData, setFormData] = useState(defaultHours);
  const [originalData, setOriginalData] = useState(defaultHours);

  // Generate time options for the dropdown (15-minute intervals)
  const generateTimeOptions = () => {
    const options = [];
    const startTime = moment().startOf('day');
    const endTime = moment().endOf('day');

    while (startTime <= endTime) {
      options.push({
        value: startTime.format('HH:mm'),
        label: startTime.format('h:mm A')
      });
      startTime.add(15, 'minutes');
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  useEffect(() => {
    if (initialData) {
      const filteredData = { "id": initialData.id };
      for (const key in defaultHours) {
        filteredData[key] = convertTo24HourFormat(initialData[key]);
      }
      setFormData(filteredData);
      setOriginalData(filteredData);
      setIsLoading(false);
    }
  }, [initialData]);

  // Check if form data has changed from original
  const hasFormChanged = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  // Calculate hours between start and end time
  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    const start = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');
    const duration = moment.duration(end.diff(start));
    const hours = duration.asHours();
    return `${hours} hours`;
  };

  const days = [
    { key: 'sunday', label: 'Sunday' },
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!hasFormChanged()) {
      onClose?.();
      onSubmit(null);
      return;
    }

    const convertedData = { ...formData };
    for (const key in formData) {
      if (key.includes('_time') && formData[key]) {
        convertedData[key] = moment(formData[key], 'HH:mm').format('h:mm A');
      }
    }

    onSubmit(convertedData);
  };

  return (
    <Paper p="md" w="100%">
      <LoadingOverlay visible={isLoading} />
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          {days.map(({ key, label }) => {
            const isEnabled = !!(formData[`${key}_open_time`] || formData[`${key}_end_time`]);
            const startTime = formData[`${key}_open_time`];
            const endTime = formData[`${key}_end_time`];
            const hoursText = calculateHours(startTime, endTime);
            
            return (
              <Grid key={key} align="center">
                <Grid.Col span={isMobile ? 12 : 3} pt={isMobile ? "0" : "xs"} pb={isMobile ? "0" : "xs"}>
                  <Checkbox
                    label={label}
                    checked={isEnabled}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        [`${key}_open_time`]: e.currentTarget.checked ? "09:00" : null,
                        [`${key}_end_time`]: e.currentTarget.checked ? "17:00" : null,
                      });
                    }}
                    styles={{
                      label: { fontWeight: 500 }
                    }}
                  />
                </Grid.Col>
                {isEnabled ? (
                  <Grid.Col span={isMobile ? 12 : 9} pt={isMobile ? "0" : "xs"} pb="md">
                    <Group position="left" gap={isMobile ? "xs" : "sm"} noWrap pl={isMobile ? "xl" : "0"}>
                      <Select
                        value={startTime}
                        data={timeOptions}
                        size={isMobile ? "xs" : "sm"}
                        searchable
                        clearable
                        onChange={(value) => {
                          setFormData({
                            ...formData,
                            [`${key}_open_time`]: value
                          });
                        }}
                        styles={{ 
                          root: { width: 'auto' },
                          input: { width: '120px' }
                        }}
                      />
                      <Text size="sm" color="dimmed" sx={{ whiteSpace: 'nowrap' }}>
                        to
                      </Text>
                      <Select
                        value={endTime}
                        data={timeOptions}
                        searchable
                        clearable
                        onChange={(value) => {
                          setFormData({
                            ...formData,
                            [`${key}_end_time`]: value
                          });
                        }}
                        styles={{ 
                          root: { width: 'auto' },
                          input: { width: '120px' }
                        }}
                      />
                      {hoursText && !isMobile && (
                        <Text size="sm" color="dimmed" sx={{ whiteSpace: 'nowrap' }}>
                          {hoursText}
                        </Text>
                      )}
                    </Group>
                  </Grid.Col>
                ) : (
                  <Grid.Col span={isMobile ? 12 : 9} >
                    { !isMobile &&<Text color="dimmed">Unavailable</Text> }
                  </Grid.Col>
                )}
              </Grid>
            );
          })}

          <div className="form-group">
            <Button 
              type="submit" 
              fullWidth 
              size="md" 
              mt={isMobile ? "sm" : "xl"}
              color="violet"
              mb="md"
            >
              {buttonText}
            </Button>
            {includeSkip && (
              <Button
                type="button"
                fullWidth
                size="md"
                variant="light"
                color="gray"
                onClick={() => onSubmit(null)}
              >
                Skip for Now
              </Button>
            )}
          </div>
        </Stack>
      </form>
    </Paper>
  );
}

export default BusinessHoursForm;